/**
 * API client for beta access endpoints
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Access status types
 */
export type AccessStatus = 'allowed' | 'pending' | 'none';

/**
 * Use case options for beta access requests
 */
export const USE_CASE_OPTIONS = [
  'Academic',
  'Product research',
  'Prompt engineering',
  'Writing',
  'Marketing',
  'Policy review',
  'Education',
  'Other'
] as const;

export type UseCase = typeof USE_CASE_OPTIONS[number];

/**
 * Response from GET /api/access/public-status
 */
export interface PublicStatusResponse {
  status: AccessStatus;
}

/**
 * Request body for POST /api/access/request
 */
export interface AccessRequestBody {
  email: string;
  name: string;
  useCase: UseCase;
  additionalDetails?: string;
}

/**
 * Response from POST /api/access/request
 */
export interface AccessRequestResponse {
  id: string;
  email: string;
  status: 'pending' | 'approved' | 'denied';
  message: string;
}

/**
 * Error response from API
 */
export interface ApiErrorResponse {
  error: string;
  details?: { path?: string[]; message?: string }[];
  message?: string;
}

/**
 * Check if an email has beta access
 *
 * @param email - Email address to check
 * @returns Promise with access status
 */
export async function checkAccessStatus(email: string): Promise<PublicStatusResponse> {
  const params = new URLSearchParams({ email: email.trim() });
  const response = await fetch(`${API_BASE_URL}/api/access/public-status?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error: ApiErrorResponse = await response.json();
    throw new Error(error.error || 'Failed to check access status');
  }

  return response.json();
}

/**
 * Submit a beta access request
 *
 * @param data - Access request data
 * @returns Promise with request response
 */
export async function submitAccessRequest(data: AccessRequestBody): Promise<AccessRequestResponse> {
  const response = await fetch(`${API_BASE_URL}/api/access/request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: data.email.trim(),
      name: data.name.trim(),
      useCase: data.useCase,
      additionalDetails: data.additionalDetails?.trim() || undefined,
    }),
  });

  if (!response.ok) {
    const error: ApiErrorResponse = await response.json();

    // Extract validation errors if present
    if (error.details && Array.isArray(error.details)) {
      const messages = error.details.map(d => d.message || d).join(', ');
      throw new Error(messages);
    }

    throw new Error(error.message || error.error || 'Failed to submit access request');
  }

  return response.json();
}

/**
 * Validate email format (client-side)
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Validate name (client-side)
 */
export function isValidName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length >= 1 && trimmed.length <= 100;
}

/**
 * Validate additional details (client-side)
 */
export function isValidAdditionalDetails(details: string): boolean {
  return details.trim().length <= 1000;
}
